/**
 * 赠送兑付（Alice 页面 giftRedemption 的复刻）：赠送出去的花瓣有多少被用掉，剩下的有多少还可能变成真实成本。
 *
 * 背景：Alice 按「发放事由」归批、渠道是它自己的四种发放方式；本仓的数据全部从赠送本账（wallet_bonus）的流水算出：
 *   正数入账是发放，计费扣减是兑付，退款追回另计，按流水单号前缀分渠道（充值赠送、用量福利、账号赠礼、管理员发放、
 *   激活码、冲正返还、其他），所以比 Alice 多一个「充值赠送」渠道，批次里充值赠送按活动批次归组。
 * 设计意图：版式、六张指标卡、活跃窗口筛选与三张表的列顺序与 Alice 一致；面值按 1 美元 = 1,000 花瓣折算，
 *   发放面值不是成本，只有被消耗掉的部分才产生上游支出，所以未兑付按持有者近期是否还在调用拆成活跃与沉睡。
 * 关键约束：内部账号（有倍率为 0 的密钥）不计入对外负债，单独一张卡显示；点击持有者进入该账号详情。
 */
import { useState } from 'react'
import { campaignApi } from '@ap/api-client/admin-campaign'
import { AG_campaign as G, errorTextC } from '@ap/i18n/admin-campaign'
import { intlTag, useI18n } from '@ap/i18n'
import { fmtDateTime, navigate, useLoad } from '@ap/ui'
import { OpsPage, Stat, Stats } from '../../alice/kit.tsx'
import { ToolSelect, fmtNum, fmtPetalsUsd } from '../../alice/kit-campaign.tsx'

/** 兑付率条：40% 以上绿、15% 以上黄、其余红（与 Alice 一致）。 */
function RateBar({ rate }: { rate: string }) {
  const r = Math.max(0, Math.min(100, parseFloat(rate) || 0))
  const color = r >= 40 ? 'var(--success-text)' : r >= 15 ? 'var(--warning-text)' : 'var(--error-text)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ flex: '0 0 42px', fontVariantNumeric: 'tabular-nums' }}>{r.toFixed(1)}%</span>
      <span style={{ flex: 1, minWidth: 36, height: 4, background: 'var(--border-medium)', borderRadius: 2, overflow: 'hidden', display: 'inline-block' }}>
        <span style={{ display: 'block', width: `${r}%`, height: '100%', background: color }} />
      </span>
    </div>
  )
}

export function GiftRedemption() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const [days, setDays] = useState(7)
  const { data, error, loading, reload } = useLoad(() => campaignApi.redemption(days), [days])
  const s = data?.summary
  const dash = t(G.common.dash)
  const pu = (v: number | undefined) => (v === undefined ? dash : fmtPetalsUsd(v, tag))
  const channel = (c: string) => (c in G.redeem.channels ? t(G.redeem.channels[c]) : c)
  const emptyRow = (cols: number) => (
    <tr>
      <td colSpan={cols} className="empty">
        {error ? errorTextC(error, locale) : loading ? t(G.common.loading) : t(G.redeem.empty)}
      </td>
    </tr>
  )

  return (
    <OpsPage eyebrow={t(G.redeem.eyebrow)} title={t(G.redeem.title)} desc={t(G.redeem.desc)} chip={t(G.redeem.chip)}>
      <Stats>
        <Stat label={t(G.redeem.granted)} value={pu(s?.granted_petals)} sub={s ? t(G.redeem.grantedSub, { n: fmtNum(s.users, tag) }) : ''} />
        <Stat label={t(G.redeem.redeemed)} value={pu(s?.redeemed_petals)} color="success" sub={s ? t(G.redeem.rateSub, { r: s.redeem_rate }) : ''} />
        <Stat label={t(G.redeem.outstanding)} value={pu(s?.outstanding_petals)} sub={t(G.redeem.outstandingSub)} />
        <Stat
          label={t(G.redeem.live)}
          value={pu(s?.live_outstanding_petals)}
          color="warning"
          sub={s && data ? t(G.redeem.liveSub, { n: fmtNum(s.live_users, tag), d: data.active_days }) : ''}
        />
        <Stat label={t(G.redeem.dormant)} value={pu(s?.dormant_outstanding_petals)} sub={s ? t(G.redeem.dormantSub, { n: fmtNum(s.dormant_users, tag), p: s.dormant_share }) : ''} />
        <Stat label={t(G.redeem.internal)} value={pu(s?.internal_granted_petals)} sub={s ? t(G.redeem.internalSub, { n: fmtNum(s.internal_users, tag) }) : t(G.redeem.internalSub, { n: 0 })} />
      </Stats>

      <div className="ops-toolbar">
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.redeem.activeWindow)}</span>
        <ToolSelect value={String(days)} onChange={(e) => setDays(Number(e.target.value))} options={[7, 14, 30].map((d) => ({ value: String(d), label: t(G.redeem.activeOption(d)) }))} />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.redeem.activeHint)}</span>
        <button className="btn btn-secondary btn-sm" type="button" style={{ marginLeft: 'auto' }} onClick={reload}>
          {t(G.common.refresh)}
        </button>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.redeem.channelsTitle)}</div>
            <div className="ops-section-copy">{t(G.redeem.channelsCopy)}</div>
          </div>
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.redeem.channelCols.channel)}</th>
                <th>{t(G.redeem.channelCols.users)}</th>
                <th>{t(G.redeem.channelCols.granted)}</th>
                <th>{t(G.redeem.channelCols.redeemed)}</th>
                <th>{t(G.redeem.channelCols.rate)}</th>
                <th>{t(G.redeem.channelCols.outstanding)}</th>
                <th>{t(G.redeem.channelCols.live)}</th>
                <th>{t(G.redeem.channelCols.dormant)}</th>
              </tr>
            </thead>
            <tbody>
              {data && data.channels.length > 0
                ? data.channels.map((c) => (
                    <tr key={c.channel}>
                      <td>{channel(c.channel)}</td>
                      <td>{fmtNum(c.users, tag)}</td>
                      <td>{pu(c.granted_petals)}</td>
                      <td style={{ color: 'var(--success-text)' }}>{pu(c.redeemed_petals)}</td>
                      <td style={{ minWidth: 120 }}>
                        <RateBar rate={c.redeem_rate} />
                      </td>
                      <td>{pu(c.outstanding_petals)}</td>
                      <td style={{ color: 'var(--warning-text)' }}>{pu(c.live_outstanding_petals)}</td>
                      <td style={{ color: 'var(--text-tertiary)' }}>{pu(c.dormant_outstanding_petals)}</td>
                    </tr>
                  ))
                : emptyRow(8)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.redeem.batchesTitle)}</div>
            <div className="ops-section-copy">{t(G.redeem.batchesCopy)}</div>
          </div>
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.redeem.batchCols.batch)}</th>
                <th>{t(G.redeem.batchCols.channel)}</th>
                <th>{t(G.redeem.batchCols.users)}</th>
                <th>{t(G.redeem.batchCols.live)}</th>
                <th>{t(G.redeem.batchCols.granted)}</th>
                <th>{t(G.redeem.batchCols.redeemed)}</th>
                <th>{t(G.redeem.batchCols.rate)}</th>
                <th>{t(G.redeem.batchCols.outstanding)}</th>
                <th>{t(G.redeem.batchCols.dormant)}</th>
                <th>{t(G.redeem.batchCols.last)}</th>
              </tr>
            </thead>
            <tbody>
              {data && data.batches.length > 0
                ? data.batches.map((b) => {
                    const name = b.batch || t(G.redeem.batchDefault)
                    return (
                      <tr key={`${b.channel}:${b.batch}`}>
                        <td title={name} style={{ maxWidth: 240 }}>
                          <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{name}</span>
                        </td>
                        <td style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{channel(b.channel)}</td>
                        <td>{fmtNum(b.users, tag)}</td>
                        <td>{fmtNum(b.live_users, tag)}</td>
                        <td>{pu(b.granted_petals)}</td>
                        <td style={{ color: 'var(--success-text)' }}>{pu(b.redeemed_petals)}</td>
                        <td style={{ minWidth: 120 }}>
                          <RateBar rate={b.redeem_rate} />
                        </td>
                        <td>{pu(b.outstanding_petals)}</td>
                        <td style={{ color: 'var(--text-tertiary)' }}>{pu(b.dormant_outstanding_petals)}</td>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--text-tertiary)', fontSize: 12 }}>{b.last_at ? fmtDateTime(b.last_at, locale) : dash}</td>
                      </tr>
                    )
                  })
                : emptyRow(10)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.redeem.topTitle)}</div>
            <div className="ops-section-copy">{t(G.redeem.topCopy)}</div>
          </div>
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.redeem.topCols.user)}</th>
                <th>{t(G.redeem.topCols.granted)}</th>
                <th>{t(G.redeem.topCols.redeemed)}</th>
                <th>{t(G.redeem.topCols.rate)}</th>
                <th>{t(G.redeem.topCols.outstanding)}</th>
                <th>{t(G.redeem.topCols.balance)}</th>
                <th>{t(G.redeem.topCols.lastSeen)}</th>
                <th>{t(G.redeem.topCols.status)}</th>
              </tr>
            </thead>
            <tbody>
              {data && data.top_outstanding.length > 0
                ? data.top_outstanding.map((h) => (
                    <tr key={h.account_id}>
                      <td>
                        <span className="mono" style={{ cursor: 'pointer', textDecoration: 'underline dotted' }} title={h.account_id} onClick={() => navigate(`/accounts/${h.account_id}`)}>
                          {h.account_name}
                        </span>
                      </td>
                      <td>{pu(h.granted_petals)}</td>
                      <td style={{ color: 'var(--success-text)' }}>{pu(h.redeemed_petals)}</td>
                      <td style={{ minWidth: 120 }}>
                        <RateBar rate={h.redeem_rate} />
                      </td>
                      <td>{pu(h.outstanding_petals)}</td>
                      <td>{pu(h.avail_petals)}</td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-tertiary)', fontSize: 12 }}>{h.last_seen ? fmtDateTime(h.last_seen, locale) : t(G.redeem.neverCalled)}</td>
                      <td>
                        {h.is_live ? (
                          <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 11, background: 'var(--warning-bg)', color: 'var(--warning-text)' }}>{t(G.redeem.live_)}</span>
                        ) : (
                          <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 11, background: 'var(--border-light)', color: 'var(--text-tertiary)' }}>{t(G.redeem.dormant_)}</span>
                        )}
                      </td>
                    </tr>
                  ))
                : emptyRow(8)}
            </tbody>
          </table>
        </div>
      </div>
    </OpsPage>
  )
}
